export interface FaqLink {
  label: string;
  href: string;
}

export interface FaqEntry {
  id: string;
  q: string;
  a: string;
  link?: FaqLink;
}

export interface FaqSection {
  title?: string;
  entries: readonly FaqEntry[];
}

/** The /faq page's own chrome, covered by content.test.ts like every sibling string. */
export const FAQ_PAGE = {
  title: "Questions, answered",
} as const;

const identity = {
  id: "engine",
  q: "What is GoodSender?",
  a: "Your blog. Your voice. Publish everywhere. GoodSender brings your blog, newsletter, and audience together in one workflow. Write in the editor or connect an AI through MCP or CLI, with Skills guiding its workflow.",
} as const;

const publishingWithAi = [
  {
    id: "claude-code",
    q: "How do I publish a blog post from Claude Code?",
    a: "Install GoodSender's official MCP server in Claude Code, connect it to your workspace, then use it to create a draft or preview. Check the selected destinations and review the post in the dashboard before publishing. When MCP action guards are enabled, publishing waits for your confirmation. The MCP guide explains the connection and first-publish flow.",
    link: { label: "MCP guide", href: "/docs/mcp/" },
  },
  {
    id: "claude-newsletter",
    q: "Can Claude publish blog posts and send them as a newsletter for me?",
    a: "Claude can create drafts and prepare a selected newsletter through GoodSender. Publishing uses the destinations selected for that post; a selected newsletter sends a summary and link once, when the sender is ready and recipients have consented. Review those choices in the dashboard, or use the guarded MCP flow and confirm it. CLI and Skills follow their own workflow conventions.",
  },
  {
    id: "chatgpt-domain",
    q: "Can ChatGPT write and publish posts to my blog on my own domain?",
    a: "ChatGPT can help write, edit, preview, and publish posts when you add GoodSender's hosted MCP server as a ChatGPT app; publishing needs a ChatGPT plan that allows MCP apps with write actions. Your blog can use a GoodSender-supplied address or a connected custom domain, so a custom domain is not mandatory. ChatGPT asks you to confirm write actions, and the dashboard review shows the post and its destinations.",
  },
  {
    id: "official-mcp",
    q: "Is there a blog platform with an official MCP server?",
    a: "Yes. GoodSender has an official MCP server for its blog workflow. It works with assistants that support remote MCP, and an MCP bundle is available for clients that support bundles. Use the MCP guide to connect an assistant, choose OAuth for hosted MCP, or use the documented local bundle and API-key alternative.",
    link: { label: "MCP guide", href: "/docs/mcp/" },
  },
  {
    id: "mcp-tools",
    q: "Which MCP tools does GoodSender have, and which need my approval?",
    a: "list_posts, get_post, and get_post_distribution read your blog and selected destinations. create_post, edit_post, and create_post_preview prepare work; editing does not publish or resend. set_post_distribution chooses destinations. publish_post, broadcast_post, send_post_test, and reannounce_post affect delivery. With MCP action guards enabled, a change waits for confirm_action; the CLI disables these guards, while Skills guide your workflow. The MCP guide has details.",
    link: { label: "MCP guide", href: "/docs/mcp/" },
  },
  {
    id: "manual",
    q: "Do I have to write with AI?",
    a: "No. You can write and publish directly in GoodSender's editor without an AI assistant. To work from Claude or ChatGPT, connect through MCP; Claude Code and Codex can also use the CLI, with Skills guiding the workflow. Either way, you decide what to write, which destinations to choose, and which review or confirmation process fits the connection you use.",
  },
  {
    id: "pricing",
    q: "How much does it cost?",
    a: "GoodSender is free for up to 100,000 emails each month. The next tier is $10 a month for up to 1,000,000 emails, then $10 for each additional million. Pricing is based on email volume, not subscriber count. See the pricing page for the current bands and the details included with your account.",
  },
] as const;

const blogAndNewsletter = [
  {
    id: "deliverability",
    q: "How do I know my email will actually land?",
    a: "Because deliverability is what we do. GoodSender comes from the team behind SendGrid, and it sends on Laneful, enterprise infrastructure from that same team, so the deliverability is ours to own rather than rent. Every list is double opt-in and self-cleaning, which is what keeps one shared pool healthy enough to carry every sender on it.",
  },
  {
    id: "domain",
    q: "Do I need my own domain?",
    a: "No. Your blog can use a GoodSender-supplied address, or you can connect a custom domain when you want your own address. A custom domain is optional, and the audience and SEO equity stay attached to the address you choose. Connect one when it fits your brand and publishing plans.",
  },
  {
    id: "seo",
    q: "Will search engines and AI assistants actually find my posts?",
    a: "They can read them, which is the part we control. Every post is served as fast, server-rendered HTML at the blog address you choose, whether that is a GoodSender-supplied address or a connected custom domain. Clean URLs, canonical tags, a sitemap, and an RSS feed give crawlers the text itself rather than a JavaScript shell, and authority builds around the address you choose.",
  },
  {
    id: "monetize",
    q: "Can I monetize my newsletter with paid subscriptions?",
    a: "No, and that's deliberate. We're for businesses that make money from their product, not from the newsletter. If you want paywalls and ads, another tool will fit you better.",
  },
  {
    id: "editor",
    q: "Do I have to use the editor, or can I publish from code?",
    a: "Either. Write in the editor, or use MCP or CLI with your AI to prepare and save your work in GoodSender. Choose the review or confirmation workflow that applies to the connection you use before publishing or sending.",
  },
  {
    id: "hygiene",
    q: "What happens to subscribers who never open anything?",
    a: "After 120 days of no engagement we ask them to re-confirm. People who don't are suppressed. That's how your list stays clean and your reputation stays high.",
  },
  {
    id: "migrate",
    q: "Can I bring my existing blog and list?",
    a: "Your list, yes: import your subscribers in bulk. Every imported address is treated as unconfirmed and receives a consent request before any newsletter reaches them, so an old list gets re-confirmed rather than blasted. Your posts: the editor is markdown, so a post pastes in cleanly, but a bulk post importer is not built yet.",
  },
  {
    id: "ghost",
    q: "How is this different from Ghost?",
    a: "Ghost and GoodSender take different approaches. GoodSender brings a blog, newsletter, and sending workflow together for businesses that publish under their own voice. It uses send-based pricing rather than per-subscriber pricing and does not require self-hosting. If paid subscriptions, paywalls, or ads are your core business, choose the product that fits that publication model.",
  },
] as const;

const developerFaq = [
  {
    id: "transactional",
    q: "Can I send my product's transactional email here too?",
    a: "Yes, over the same API. Receipts, password resets, and alerts go out on the same engine and the same sending reputation as your newsletter, from a catalog of ready templates you can brand with your own colors. Transactional mail is not marketing, so it does not run through the subscribe flow. It is one vendor and one integration instead of two.",
  },
] as const;

/** The flat deck feeds pricing IDs and FAQPage structured data. */
export const FAQ = [
  identity,
  ...publishingWithAi,
  ...blogAndNewsletter,
  ...developerFaq,
] as const satisfies readonly FaqEntry[];

/** This groups the exact same entries only for /faq presentation. */
export const FAQ_SECTIONS = [
  { entries: [identity] },
  { title: "Publishing with AI", entries: publishingWithAi },
  { title: "Your blog, newsletter, and audience", entries: blogAndNewsletter },
  { title: "For developers using the API", entries: developerFaq },
] as const satisfies readonly FaqSection[];

/** IDs stay literal so pricing IDs are checked against this deck at compile time. */
export type FaqId = (typeof FAQ)[number]["id"];
